import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api.ts';

const CancelModal = ({ isOpen, onClose, orderId, email, needsRefund, initialPaymentData, onSuccess }) => {
    const [paymentData, setPaymentData] = useState({ banco: '', identificacion: '', telefono: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && initialPaymentData && needsRefund) {
            setPaymentData(prev => ({
                ...prev,
                banco: initialPaymentData.banco || prev.banco,
                identificacion: initialPaymentData.identificacion || prev.identificacion,
                telefono: initialPaymentData.telefono || prev.telefono
            }));
        }
    }, [isOpen, initialPaymentData, needsRefund]);

    if (!isOpen) return null;

    const handleCancel = async () => {
        if (needsRefund && (!paymentData.banco || !paymentData.identificacion || !paymentData.telefono)) {
            return toast.error('Necesitamos tus datos para devolverte el dinero');
        }

        setIsSubmitting(true);
        try {
            await api.post(`/api/orders/${orderId}/cancel`, {
                email,
                payment_data: needsRefund ? paymentData : null
            });
            toast.success('Pedido cancelado correctamente');
            onSuccess();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al cancelar');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
                <div className="flex justify-center mb-4">
                    <div className="p-3 bg-red-500/10 rounded-full border border-red-500/20">
                        <AlertTriangle className="h-6 w-6 text-red-400" />
                    </div>
                </div>
                <h3 className="text-lg font-bold text-white text-center mb-2">¿Cancelar Pedido?</h3>
                <p className="text-xs text-white/50 text-center mb-6">Esta acción no se puede deshacer.</p>

                {needsRefund && (
                    <div className="mb-6 bg-red-900/10 border border-red-500/20 rounded-xl p-3 space-y-2">
                        <p className="text-[10px] font-bold text-red-400 uppercase mb-1 text-center">Datos para tu reembolso</p>
                        
                        <select 
                            value={paymentData.banco} 
                            onChange={e => setPaymentData({...paymentData, banco: e.target.value})} 
                            className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:border-red-500 outline-none cursor-pointer"
                        >
                            <option value="">Selecciona tu banco</option>
                            <option value="Banco de Venezuela (0102)">Banco de Venezuela (0102)</option>
                            <option value="BBVA Provincial (0108)">BBVA Provincial (0108)</option>
                            <option value="Banesco (0134)">Banesco (0134)</option>
                            <option value="Mercantil (0105)">Mercantil (0105)</option>
                            <option value="Banco Nacional de Crédito (0191)">Banco Nacional de Crédito - BNC (0191)</option>
                            <option value="Bancaribe (0114)">Bancaribe (0114)</option>
                            <option value="Bancamiga (0172)">Bancamiga (0172)</option>
                            <option value="Banplus (0174)">Banplus (0174)</option>
                            <option value="Banco Venezolano de Crédito (0104)">Banco Venezolano de Crédito - BVC (0104)</option>
                            <option value="Exterior (0115)">Exterior (0115)</option>
                            <option value="100% Banco (0156)">100% Banco (0156)</option>
                            <option value="Del Sur (0157)">Del Sur (0157)</option>
                            <option value="Banco Activo (0171)">Banco Activo (0171)</option>
                            <option value="Sofitasa (0137)">Sofitasa (0137)</option>
                            <option value="Banco Plaza (0138)">Banco Plaza (0138)</option>
                            <option value="Banco del Tesoro (0163)">Banco del Tesoro (0163)</option>
                            <option value="Banco Agrícola de Venezuela (0166)">Banco Agrícola de Venezuela (0166)</option>
                            <option value="BANFANB (0177)">BANFANB (0177)</option>
                            <option value="Bicentenario (0175)">Bicentenario (0175)</option>
                            <option value="Bangente (0146)">Bangente (0146)</option>
                            <option value="Bancrecer (0168)">Bancrecer (0168)</option>
                            <option value="Mi Banco (0169)">Mi Banco (0169)</option>
                        </select>
                        
                        <div className="grid grid-cols-2 gap-2">
                            <input placeholder="C.I / RIF" value={paymentData.identificacion} onChange={e => setPaymentData({...paymentData, identificacion: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:border-red-500 outline-none" />
                            <input placeholder="Teléfono" value={paymentData.telefono} onChange={e => setPaymentData({...paymentData, telefono: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:border-red-500 outline-none" />
                        </div>
                    </div>
                )}

                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-white/10 text-white/60 text-xs font-bold uppercase tracking-widest hover:bg-white/5 transition-colors">Volver</button>
                    <button onClick={handleCancel} disabled={isSubmitting} className="flex-1 py-3 rounded-xl bg-red-600 text-white text-xs font-bold uppercase tracking-widest hover:bg-red-500 disabled:opacity-50 transition-colors">
                        {isSubmitting ? 'Procesando...' : 'Sí, Cancelar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CancelModal;